import React, { useState } from "react";

import {
	AppBar,
	IconButton,
	Toolbar,
	Typography,
	Drawer,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	ListItemSecondaryAction,
	Paper,
	Slide,
	makeStyles,
} from "@material-ui/core";

import CloudOffIcon from "@material-ui/icons/CheckCircle";
import CloudOnIcon from "@material-ui/icons/CheckCircleOutline";
import ArrowBackIos from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIos from "@material-ui/icons/ArrowForwardIos";
import PresentToAllIcon from "@material-ui/icons/PresentToAll";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import PDFIcon from "@material-ui/icons/PictureAsPdf";
import DescriptionIcon from '@material-ui/icons/Description';
import PresentSlidesIcon from "@material-ui/icons/Panorama";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import PrintIcon from "@material-ui/icons/Print";
import ShareIcon from "@material-ui/icons/Share";
import FolderIcon from "@material-ui/icons/Folder";
import StarIcon from "@material-ui/icons/Star";
import OfflineIcon from "@material-ui/icons/CheckCircle";
import SaveIcon from "@material-ui/icons/FavoriteBorder";
import { useEditor } from "../editor/useEditor";
import { ExportHtml } from "../../../utilities/ExportHtml";
import { EventTracking, OfflineActionLocation, PrintActions } from "../../../utilities/Tracking/EventTracking";
import { TelemetryService } from "../../../services/TelemetryService";
import { BuilderAppBarAction, useBuilder } from "../useBuilder";
import { MobileBuilderAppSelection } from "../drawer/apps/MobileBuilderAppSelection";
import { IOSBuilderView } from "./IOSBuilderView";
import { AndroidBuilderView } from "./AndroidBuilderView";
import { useOfflineAssets } from "../../offline/useOfflineAssets";
import { BuilderAsset } from "../../../entities/BuilderAsset";
import { BuilderAssetService } from "../../../services/BuilderAssetService";
import { ServerResponse } from "../../../services/server/OldWebClient";
import { exportSlides } from "../../../utilities/ExportSlides";
import { ReactComponent as OneColumn } from "../../../resources/oneColumn.svg";
import { ReactComponent as TwoColumns } from "../../../resources/twoColumns.svg";
import { ReactComponent as ThreeColumns } from "../../../resources/threeColumns.svg";
import PPTIcon from "../../../resources/file-ppt.png";
import { UserAgent } from "../../../utilities/UserAgent";

const useStyles = makeStyles((theme) => ({
	appBar: {
		color: "#6b6b6b",
		background: "#FFFFFF",
		zIndex: theme.zIndex.drawer + 1,
		boxShadow: "unset",
	},
	toolBar: {
		flex: 1,
		alignItems: "flex-start",
		marginTop: "15px",
	},
	title: {
		whiteSpace: "nowrap",
		overflow: "hidden",
		width: "100%",
		textOverflow: "ellipsis",
	},
	description: {
		whiteSpace: "nowrap",
		overflow: "hidden",
		width: "100%",
		textOverflow: "ellipsis",
	},
	drawer: {
		maxWidth: "100%",
		overflow: "hidden",
		transform: "scaleY(1)",
		transformOrigin: "top",
		transition: "transform 0.26s ease",
	},
	drawerIcons: {
		color: "rgba(0, 0, 0, 0.54)",
	},
}));

interface Props {
	type: "ios" | "android";
}
const currentLocation = "Navigation Mobile";

export function MobileLayout(props: React.PropsWithChildren<Props>) {
	const builder = useBuilder();
	const { builderAsset, preferences } = builder;
	const classes = useStyles();
	const editor = useEditor();
	const [moreOptionsDrawerIsOpen, setMoreOptionsDrawerIsOpen] = useState(false);
	const [isMobileIconsDrawerOpen, setIsMobileIconsDrawerOpen] = useState(false);
	const [expandFile, setExpandFile] = useState<boolean>(false);
	const [expandDownload, setExpandDownLoad] = useState<boolean>(false);
	const [expandPresent, setExpandPresent] = useState<boolean>(false);
	const [expandPrint, setExpandPrint] = useState<boolean>(false);
	const [showMainMenu, setShowMainMenu] = useState<boolean>(true);
	const { onlineMode, setAssetOfflineMode } = useOfflineAssets();
	const hasSlides = editor.slides.length !== 0;

	const onDownloadSlidePresentation = () => {
		exportSlides(builderAsset, editor.slides, preferences);
		let now = new Date();
		TelemetryService.updateContactField("sc_cloud_slideexport", now.toString());
		EventTracking.Builder.onExportSlides(currentLocation);
	};

	const onDownloadAssetAsPdf = () => {
		BuilderAssetService.downloadAsPdf(builderAsset);
		EventTracking.Builder.onDownloadSermonAsPdf(currentLocation);
		let now = new Date();
		TelemetryService.updateContactField("sc_cloud_downloadpdf", now.toString());
	};

	const onDownloadAssetAsDocx = () => {
		BuilderAssetService.downloadAsDocx(builderAsset);
		EventTracking.Builder.onDownloadSermonAsDocx(currentLocation);
	};

	const onPrintAsset = (columns: number, action: PrintActions) => {
		EventTracking.Builder.onPrint(action, currentLocation);
		const html = editor.getHtml();
		ExportHtml.print(html, columns);
		let now = new Date();
		TelemetryService.updateContactField("sc_cloud_print", now.toString());
	};

	const toggleOfflineAsset = async (location: OfflineActionLocation) => {
		const response = await setAssetOfflineMode(builderAsset.id, !builderAsset.offlineMode, location);
		if (response) {
			builder.setBuilderAsset(response);
			setMoreOptionsDrawerIsOpen(false);
			builder.setAlert(response.offlineMode ? "Available offline" : "No longer available offline");
		}
	};

	const toggleStar = async () => {
		const updatedAsset: BuilderAsset = { ...builderAsset, isStarred: !builderAsset.isStarred };
		let response = await BuilderAssetService.save(updatedAsset);
		if (ServerResponse.isSuccess<BuilderAsset>(response)) {
			builder.setBuilderAsset(response.data);
			setMoreOptionsDrawerIsOpen(false);
			builder.setAlert(response.data.isStarred ? "Star Added" : "Star Removed");
		} else {
			builder.setAlert("Could not update Starred");
		}
	};

	const toggleFileDrawer = () => {
		setShowMainMenu(!showMainMenu);
		setExpandFile(!expandFile);
	};

	const togglePrintDrawer = () => {
		setShowMainMenu(!showMainMenu);
		setExpandPrint(!expandPrint);
	};

	const toggleDownloadDrawer = () => {
		setShowMainMenu(!showMainMenu);
		setExpandDownLoad(!expandDownload);
	};

	const togglePresentDrawer = () => {
		setShowMainMenu(!showMainMenu);
		setExpandPresent(!expandPresent);
	};

	return (
		<>
			<AppBar position="absolute" className={classes.appBar}>
				<Toolbar className={classes.toolBar}>
					<IconButton
						onClick={() => builder.handleAppBarAction(BuilderAppBarAction.GoBack, currentLocation)}
						color="inherit"
						aria-label="Menu"
					>
						<ArrowBackIos />
					</IconButton>
					<div style={{ flexGrow: 1, width: "61%" }}>
						<Typography variant="h6" className={classes.title} color="inherit">
							{builderAsset.name}{" "}
							{builderAsset.isTemplate && (
								<span
									style={{
										fontSize: "12px",
										fontWeight: "bold",
										color: "#d90000",
									}}
								>
									{" "}
									- Editing Template
								</span>
							)}
						</Typography>
						{!builderAsset.isTemplate ? (
							<Typography variant="caption" color="inherit">
								<span>
									{onlineMode
										? editor.saveInProgress
											? "Saving..."
											: editor.lastEdit
										: editor.saveInProgress
										? "Saving..."
										: editor.lastEdit + " - Offline Mode"}
								</span>
								{onlineMode ? (
									builderAsset.offlineMode ? (
										<CloudOffIcon
											style={{ paddingLeft: 5, verticalAlign: "middle", cursor: "pointer" }}
											onClick={() => toggleOfflineAsset("App Bar Icon Mobile (Builder)")}
										/>
									) : (
										<CloudOnIcon
											style={{ paddingLeft: 5, verticalAlign: "middle", cursor: "pointer" }}
											onClick={() => toggleOfflineAsset("App Bar Icon Mobile (Builder)")}
										/>
									)
								) : (
									<CloudOffIcon style={{ paddingLeft: 5, verticalAlign: "middle" }} />
								)}
							</Typography>
						) : (
							<Typography variant="body2" color="inherit" className={classes.description}>
								Description: {builderAsset.templateDescription.length > 0 ? builderAsset.templateDescription : "No Description"}
							</Typography>
						)}
					</div>
					<IconButton onClick={() => setMoreOptionsDrawerIsOpen(true)}>
						<MoreVertIcon />
					</IconButton>
				</Toolbar>
			</AppBar>
			{props.type === "ios" ? (
				<IOSBuilderView isMobileIconsDrawerOpen={isMobileIconsDrawerOpen} />
			) : (
				<AndroidBuilderView isMobileIconsDrawerOpen={isMobileIconsDrawerOpen} />
			)}
			<MobileBuilderAppSelection
				isDrawerOpen={isMobileIconsDrawerOpen}
				setIsDrawerOpen={setIsMobileIconsDrawerOpen}
				isIos={props.type === "ios"}
			/>
			<Drawer
				anchor="bottom"
				open={moreOptionsDrawerIsOpen}
				onClose={() => {
					setMoreOptionsDrawerIsOpen(false);
				}}
				className={classes.drawer}
			>
				<Paper style={{ width: "100%", overflow: "hidden" }} elevation={2}>
					{showMainMenu && (
						<Slide direction="right" in={showMainMenu}>
							<List>
								<ListItem onClick={toggleFileDrawer}>
									<ListItemIcon>
										<FolderIcon />
									</ListItemIcon>
									<ListItemText inset primary="File" />
									<ListItemSecondaryAction>
										<ArrowForwardIos onClick={toggleFileDrawer} className={classes.drawerIcons} />
									</ListItemSecondaryAction>
								</ListItem>
								<ListItem onClick={togglePresentDrawer}>
									<ListItemIcon>
										<PresentToAllIcon />
									</ListItemIcon>
									<ListItemText inset primary="Present" />
									<ListItemSecondaryAction>
										<ArrowForwardIos onClick={togglePresentDrawer} className={classes.drawerIcons} />
									</ListItemSecondaryAction>
								</ListItem>
								<ListItem onClick={toggleDownloadDrawer}>
									<ListItemIcon>
										<CloudDownloadIcon />
									</ListItemIcon>
									<ListItemText inset primary="Download" />
									<ListItemSecondaryAction>
										<ArrowForwardIos onClick={toggleDownloadDrawer} className={classes.drawerIcons} />
									</ListItemSecondaryAction>
								</ListItem>
								<ListItem onClick={togglePrintDrawer}>
									<ListItemIcon>
										<PrintIcon />
									</ListItemIcon>
									<ListItemText inset primary="Print" />
									<ListItemSecondaryAction>
										<ArrowForwardIos onClick={togglePrintDrawer} className={classes.drawerIcons} />
									</ListItemSecondaryAction>
								</ListItem>
							</List>
						</Slide>
					)}
					{expandFile && (
						<Slide direction="left" in={expandFile}>
							<List>
								<ListItem onClick={toggleFileDrawer}>
									<ListItemIcon>
										<ArrowBackIos />
									</ListItemIcon>
									<ListItemText inset primary="back" />
								</ListItem>
								{!builderAsset.isTemplate && (
									<>
										<ListItem
											onClick={() =>
												onlineMode && builder.handleAppBarAction(BuilderAppBarAction.SaveAsTemplate, currentLocation)
											}
											disabled={!onlineMode}
										>
											<ListItemIcon>
												<SaveIcon />
											</ListItemIcon>
											<ListItemText inset primary="Save As Template" />
										</ListItem>
									</>
								)}
								<ListItem onClick={() => toggleOfflineAsset("3 Dots Mobile (Builder)")}>
									<ListItemIcon>
										<OfflineIcon />
									</ListItemIcon>
									<ListItemText inset primary={builderAsset.offlineMode ? "Offline Disable" : "Offline Enable"} />
								</ListItem>
								<ListItem onClick={toggleStar}>
									<ListItemIcon>
										<StarIcon />
									</ListItemIcon>
									<ListItemText inset primary={builderAsset.isStarred ? "Remove Star" : "Add Star"} />
								</ListItem>
							</List>
						</Slide>
					)}
					{expandPresent && (
						<Slide direction="left" in={expandPresent}>
							<List>
								<ListItem onClick={togglePresentDrawer}>
									<ListItemIcon>
										<ArrowBackIos />
									</ListItemIcon>
									<ListItemText inset primary="back" />
								</ListItem>
								<ListItem onClick={() => builder.handleAppBarAction(BuilderAppBarAction.Present, currentLocation)}>
									<ListItemIcon>
										<PresentToAllIcon />
									</ListItemIcon>
									<ListItemText inset primary="Present Sermon" />
								</ListItem>
								<ListItem onClick={() => builder.toggleSlideExportDialog(currentLocation)} disabled={!hasSlides}>
									<ListItemIcon>
										<PresentSlidesIcon />
									</ListItemIcon>
									<ListItemText inset primary="Present Slides" />
								</ListItem>
							</List>
						</Slide>
					)}
					{expandDownload && (
						<Slide direction="left" in={expandDownload}>
							<List>
								<ListItem onClick={toggleDownloadDrawer}>
									<ListItemIcon>
										<ArrowBackIos />
									</ListItemIcon>
									<ListItemText inset primary="back" />
								</ListItem>
								<ListItem onClick={onDownloadAssetAsPdf}>
									<ListItemIcon>
										<PDFIcon />
									</ListItemIcon>
									<ListItemText inset primary="Download as PDF" />
								</ListItem>
								<ListItem onClick={onDownloadAssetAsDocx}>
									<ListItemIcon>
										<DescriptionIcon />
									</ListItemIcon>
									<ListItemText inset primary="Download as Docx" />
								</ListItem>
								<ListItem onClick={onDownloadSlidePresentation} disabled={!hasSlides}>
									<ListItemIcon>
										<img alt="powerpoint icon" src={PPTIcon} height={24} width={24} />
									</ListItemIcon>
									<ListItemText inset primary="Download slide presentation" />
								</ListItem>
							</List>
						</Slide>
					)}
					{expandPrint && (
						<Slide direction="left" in={expandPrint}>
							<List>
								<ListItem onClick={togglePrintDrawer}>
									<ListItemIcon>
										<ArrowBackIos />
									</ListItemIcon>
									<ListItemText inset primary="back" />
								</ListItem>
								<ListItem onClick={() => onPrintAsset(1, "Quick Print")}>
									<ListItemIcon>
										<PrintIcon />
									</ListItemIcon>
									<ListItemText inset primary="Quick Print" />
								</ListItem>
								{UserAgent.getBrowser().name !== "Safari" && (
									<>
										<ListItem onClick={() => onPrintAsset(1, "Single Column")}>
											<ListItemIcon>
												<OneColumn height={24} width={24} />
											</ListItemIcon>
											<ListItemText inset primary="Single Column" />
										</ListItem>
										<ListItem onClick={() => onPrintAsset(2, "Two Column")}>
											<ListItemIcon>
												<TwoColumns height={24} width={24} />
											</ListItemIcon>
											<ListItemText inset primary="Two Column" />
										</ListItem>
										<ListItem onClick={() => onPrintAsset(3, "Three Column")}>
											<ListItemIcon>
												<ThreeColumns height={24} width={24} />
											</ListItemIcon>
											<ListItemText inset primary="Three Columns" />
										</ListItem>
									</>
								)}
							</List>
						</Slide>
					)}
				</Paper>
			</Drawer>
		</>
	);
}